import React, { useEffect, useRef, useState } from "react";
import { Stack } from "react-bootstrap";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";
import { useSelector } from "react-redux";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";

interface WidgetProps {
  caption: string;
}

interface WorkOrderData {
  location: string;
  count: number;
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeWorkOrdersByLocation = (response: any): WorkOrderData[] => {
  console.log("Normalizing work orders by location data:", response);
  const rawData = response?.data?.widgetsData?.workOrdersByLocation ?? [];

  console.log("Raw work orders by location data:", rawData);

  const resultData = rawData
    .map((item: any, index: number) => {
      const location =
        item?.locationName ?? item?.location ?? item?.siteName ?? item?.name ?? item?.label ?? `Location ${index + 1}`;
      const count = toNumber(item?.count ?? item?.workOrdersCount ?? item?.total ?? item?.value);

      return {
        location: String(location),
        count,
      };
    })
    .sort((a: WorkOrderData, b: WorkOrderData) => b.count - a.count);

  console.log("Normalized work orders by location data:", resultData);
  return resultData;
};

const WorkOrdersLocationChart = ({ caption }: WidgetProps) => {
  const { id: loggedInUserId, isManager } = useSelector(getUserData);

  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [completedWorkData, setCompletedWorkData] = useState<WorkOrderData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!svgRef.current || completedWorkData.length === 0) return;

    const margin = { top: 16, right: 24, bottom: 36, left: 180 };
    const containerWidth = containerRef.current?.clientWidth || 620;
    const width = containerWidth - margin.left - margin.right;
    const height = Math.max(completedWorkData.length * 34, 220);

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const maxCompleted = d3.max(completedWorkData, (d) => d.count) || 0;
    const x = d3
      .scaleLinear()
      .domain([0, maxCompleted + 5])
      .range([0, width]);

    const y = d3
      .scaleBand()
      .domain(completedWorkData.map((d) => d.location))
      .range([0, height])
      .padding(0.25);

    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3
          .axisBottom(x)
          .ticks(5)
          .tickFormat((value) => `${value}`),
      )
      .selectAll("text")
      .style("font-size", "12px");

    svg.append("g").call(d3.axisLeft(y)).selectAll("text").style("font-size", "12px");

    svg
      .selectAll(".bar")
      .data(completedWorkData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", 0)
      .attr("y", (d) => y(d.location) || 0)
      .attr("width", 0)
      .attr("height", y.bandwidth())
      .attr("fill", colors.kleenway.green)
      .style("opacity", 0.9)
      .on("mouseover", function (this: SVGRectElement, event, d) {
        d3.select(this).style("opacity", 1).style("cursor", "pointer");

        const yPosition = (y(d.location) || 0) + y.bandwidth() / 2;
        const xPosition = Math.min(x(d.count) + 38, width - 24);

        const tooltip = svg
          .append("g")
          .attr("class", "tooltip")
          .attr("transform", `translate(${xPosition},${yPosition})`);

        tooltip
          .append("rect")
          .attr("x", -42)
          .attr("y", -14)
          .attr("width", 84)
          .attr("height", 20)
          .attr("fill", "rgba(0,0,0,0.8)")
          .attr("rx", 4);

        tooltip
          .append("text")
          .attr("text-anchor", "middle")
          .attr("y", 0)
          .attr("dy", "0.32em")
          .attr("fill", "white")
          .attr("font-size", "12px")
          .text(`${d.count}`);
      })
      .on("mouseout", function (this: SVGRectElement) {
        d3.select(this).style("opacity", 0.9);
        svg.selectAll(".tooltip").remove();
      })
      .transition()
      .duration(700)
      .delay((_, i) => i * 40)
      .ease(d3.easeQuadOut)
      .attr("width", (d) => x(d.count));

    svg
      .append("text")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 6)
      .style("text-anchor", "middle")
      .style("font-size", "12px")
      .style("fill", colors.grey.dark)
      .text("Completed Work Orders");
  }, [completedWorkData]);

  useEffect(() => {
    let isMounted = true;

    const fetchWorkOrdersByLocation = async () => {
      try {
        setIsLoading(true);
        const data = await FirebaseDataService.getWorkOrdersByLocations(loggedInUserId, isManager);

        if (!isMounted) return;
        setCompletedWorkData(normalizeWorkOrdersByLocation(data));
      } catch (error) {
        if (!isMounted) return;
        setCompletedWorkData([]);
        console.error("Error fetching work orders by location:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchWorkOrdersByLocation();

    return () => {
      isMounted = false;
    };
  }, [loggedInUserId, isManager]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <h1 style={{ fontWeight: 600, margin: 0 }}>{caption}</h1>
        </HeaderRow>
        <ChartContainer ref={containerRef}>
          {isLoading && <ChartStateText>Loading chart...</ChartStateText>}
          {!isLoading && completedWorkData.length === 0 && <ChartStateText>No data available</ChartStateText>}
          {!isLoading && completedWorkData.length > 0 && <svg ref={svgRef} style={{ width: "100%" }}></svg>}
        </ChartContainer>
      </Stack>
    </WidgetContainer>
  );
};

export default WorkOrdersLocationChart;

const WidgetContainer = styled.div`
  display: flex;
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  padding: 16px;
  border-radius: 6px;
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  overflow-x: auto;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const ChartStateText = styled.span`
  color: ${colors.grey.dark};
  font-size: 14px;
  font-weight: 500;
  padding: 32px 0;
`;
